/* eslint-disable @typescript-eslint/no-explicit-any */
import { QuestionEntity, ComponentSurveyAnswers } from '@/graphql/generated/graphql';
import { User } from '@/hooks/useAuthentication';
import { submitAnswer, submitSurveyUser, requestDailyQuestions } from '@/services/daily/daily-questions.service';
import { create } from 'zustand';

export interface AnswerProps {
  currentQuestion: QuestionEntity;
  option: ComponentSurveyAnswers;
  user: User;
  comment?: string;
}

interface DailyAnswerState {
  questionLoading: boolean;
  questions: QuestionEntity[];
  surveyId: string | null;
  setQuestionLoading: (loading: boolean) => void;
  setQuestions: (questions: QuestionEntity[]) => void;
  setSurveyId: (id: string | null) => void;
  onAnswer: ({
    currentQuestion,
    option,
    user,
    comment,
  }: {
    currentQuestion: QuestionEntity;
    option: ComponentSurveyAnswers;
    user: any | null;
    comment?: string;
  }) => Promise<void>;
  fetchQuestions: () => Promise<void>;
}

export const useDailyAnswerStore = create<DailyAnswerState>((set, get) => {
  return {
    questionLoading: true,
    questions: [],
    surveyId: null,
    setQuestionLoading: (loading: boolean) => set({ questionLoading: loading }),
    setQuestions: (questions: QuestionEntity[]) => set({ questions }),
    setSurveyId: (id: string | null) => set({ surveyId: id }),

    onAnswer: async ({ currentQuestion, option, user, comment }: AnswerProps) => {
      if (!user.id) return;
      await Promise.all([
        submitAnswer({
          data: {
            survey: get().surveyId || '',
            question: currentQuestion?.id,
            answer: option.scale,
            comment: comment,
            school: user.school.id.toString(),
            user: user.id.toString(),
          },
        }),
        submitSurveyUser({
          data: {
            question: currentQuestion?.id,
            user: user.id.toString() || '',
            answerDate: new Date().toISOString().split('T')[0],
          },
        }),
      ]);

      set((state) => ({
        questions: state.questions.filter((question) => question.id !== currentQuestion.id),
      }));
    },

    fetchQuestions: async () => {
      set({ questionLoading: true });
      const { data } = await requestDailyQuestions();
      if (data && data.currentSurvey && data.currentSurvey.questions && data.currentSurvey.questions.length) {
        set({
          questions: data.currentSurvey.questions as QuestionEntity[],
          surveyId: data.currentSurvey.survey ? data.currentSurvey.survey.id : null,
        });
      }
      set({ questionLoading: false });
    },
  };
});
